import React from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import Audio from './Audio'
import Video from './Video'
import Image from './Image'
import Other from './Other'
import DownloadButton from './DownloadButton'
import CloseButton from '../../ChatWindowV2/ChatBox/Attachments/CloseButton'

import { track } from '@SonarCX/lib/mixpanel'
import { formatForUpload } from '../helpers'
import { putUser } from '@SonarCX/lib/api'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useMediaHandle from '@SonarCX/hooks/useMediaHandle'
import useStyles from './styles'

type MediaProps = {
  media: Media
  user: LocalUser
  className?: string
  setViewingFile: (file: Media) => void
}

const Media: React.FC<MediaProps> = ({
  media,
  user,
  className,
  setViewingFile
}) => {
  const dispatch = useAppDispatch()

  const { setTitle, setReason, setSubtext, setV2, setAction } = useAreYouSure()
  const mediaHandle = useMediaHandle({ media })

  const [hovering, setHovering] = React.useState(false)

  const classes = useStyles()

  const { type } = media

  const deleteMedia = React.useCallback(async () => {
    try {
      // Remove file
      const updated = formatForUpload(user)
      updated.media_ids = updated.media_ids.filter(id => id !== media.id)

      track('DELETE_USER_FILE', { delete_user_id: user.id, file_id: media.id })

      // Save update
      await putUser(updated)

      dispatch(AddSuccessAlert('Changes saved', 1500))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }, [user, media, dispatch])

  const handleDelete = React.useCallback(() => {
    setTitle(`Delete ${media.filename}`)
    setReason(' ')
    setSubtext('Are you sure you want to delete this media?')
    setV2(true)
    setAction(() => {
      deleteMedia()
    })
  }, [setTitle, setReason, media, setSubtext, setV2, setAction, deleteMedia])

  const handleDownload = React.useCallback(() => {
    mediaHandle.download()
  }, [mediaHandle])

  const handleClick = React.useCallback(() => {
    setViewingFile(media)
  }, [media, setViewingFile])

  const handleHoverEnter = React.useCallback(() => {
    setHovering(true)
  }, [])

  const handleHoverLeave = React.useCallback(() => {
    setHovering(false)
  }, [])

  return (
    <>
      <div
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        className={clsx(classes.root, className)}
      >
        {type === 'audio' && (
          <Audio mediaHandle={mediaHandle} onClick={handleClick} />
        )}

        {type === 'video' && (
          <Video mediaHandle={mediaHandle} onClick={handleClick} />
        )}

        {type === 'image' && (
          <Image mediaHandle={mediaHandle} onClick={handleClick} />
        )}

        {type === 'file' && (
          <Other mediaHandle={mediaHandle} onClick={handleClick} />
        )}

        {hovering && <CloseButton onDelete={handleDelete} />}

        {hovering && <DownloadButton onClick={handleDownload} />}
      </div>
    </>
  )
}

export default Media
